'use client';
import { useState } from 'react';
import Navbar from '@/components/Navbar';
import PageHero from '@/components/PageHero';
import Footer from '@/components/Footer';
import { Mail, Phone, MapPin, Clock, Send, ChevronRight, CheckCircle, MessageSquare, Headphones, Building } from 'lucide-react';

const CONTACT_METHODS = [
  {
    icon: Headphones,
    title: 'Atención al cliente 24/7',
    desc: 'Nuestro equipo de soporte al cliente está disponible las 24 horas y los 7 días de la semana para ayudarle con cualquier tipo de problema de la reserva.',
    action: '+1-800-986-4713',
    actionLabel: 'Llamar ahora',
    href: 'tel:+18009864713',
    color: 'bg-sky-50 text-sky-500',
  },
  {
    icon: Mail,
    title: 'Envíanos un mensaje',
    desc: 'Nuestro equipo está listo para atender sus consultas y brindarle la asistencia que necesita. Respondemos en un máximo de 4 horas laborales.',
    action: 'support@vuelosglobales.com',
    actionLabel: 'Enviar correo',
    href: 'mailto:support@vuelosglobales.com',
    color: 'bg-emerald-50 text-emerald-500',
  },
  {
    icon: MessageSquare,
    title: 'Chatea en vivo',
    desc: 'Inicia un chat con nuestros especialistas y recibe asistencia rápida, generalmente en unos 2 minutos.',
    action: 'Iniciar un chat',
    actionLabel: 'Chatear ahora',
    href: '/contacta-con-nosotros',
    color: 'bg-violet-50 text-violet-500',
  },
];

const OFFICES = [
  { city: 'Springfield (HQ)', address: '742 Evergreen Terrace, Apt 5B, Springfield, IL 62704, United States', phone: '+1-800-986-4713' },
  { city: 'España', address: 'Atención al cliente internacional', phone: '+34-868-26-9023' },
  { city: 'México', address: 'Atención al cliente internacional', phone: '+52-998-609-0037' },
];

export const metadata = {
  title: 'Contacta con nosotros | Vuelosglobales',
  description: 'Marcar el número de teléfono o enviar un mensaje a nuestro correo electrónico para obtener asistencia rápidamente.',
  alternates: { canonical: '/contacta-con-nosotros' },
  robots: { index: true, follow: true },
};

export default function ContactClient() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  function handleChange(e) {
    setForm(v => ({ ...v, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1200)); // simulate send
    setSubmitted(true);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className='hero-contact'>
        <PageHero
          image="/images/contact-hero.jpg"
          imageAlt="Contacta a Vuelosglobales"
          breadcrumb={[{ label: 'Contáctanos' }]}
          align="left"
          title="Contáctanos"
          subtitle="Le acompañamos en cada etapa de su viaje"
        />
      </div>

      {/* Contact methods */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CONTACT_METHODS.map(({ icon: Icon, title, desc, action, actionLabel, href, color }) => (
              <div key={title} className="bg-white rounded-2xl p-7 border border-slate-100 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${color}`}>
                  <Icon size={24} />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{desc}</p>
                <a href={href} className="text-sky-500 font-semibold text-sm hover:text-sky-700 transition-colors flex items-center gap-1">
                  {action} <ChevronRight size={14} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form + offices */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">

          {/* Form */}
          <div className='p-6 bg-slate-100 rounded-[10px]'>
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-3">Escríbenos</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-8">Envía un Mensaje</h2>

            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
                <CheckCircle size={52} className="text-emerald-500 mx-auto mb-4" />
                <h3 className="font-display font-bold text-2xl text-slate-900 mb-2">¡Mensaje recibido!</h3>
                <p className="text-slate-500">Nos pondremos en contacto contigo en <strong>{form.email}</strong> dentro de 4 horas hábiles.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' }); }} className="mt-6 text-sky-500 font-semibold text-sm hover:text-sky-700 transition-colors">
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Nombre Completo *</label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full border border-slate-200 bg-white text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Teléfono</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full border bg-white border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Correo Electrónico *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border bg-white border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="tu@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Asunto</label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full border border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all bg-white"
                  >
                    <option>Consulta General</option>
                    <option>Problema con la Reserva</option>
                    <option>Solicitud de Reembolso</option>
                    <option>Problema Técnico</option>
                    <option>Asociación</option>
                    <option>Prensa / Medios</option>
                    <option>Otro</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Mensaje *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all resize-none"
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Enviando…</>
                  ) : (
                    <><Send size={17} /> Enviar Mensaje</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Offices */}
          <div>
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-3">Encuéntranos</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-8">Nuestras Oficinas</h2>

            <div className="space-y-5 mb-10">
              {OFFICES.map(({ city, address, phone }) => (
                <div key={city} className="flex gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:border-sky-200 transition-colors">
                  <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                    <Building size={18} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{city}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed flex items-start gap-1.5">
                      <MapPin size={13} className="mt-0.5 shrink-0 text-slate-400" /> {address}
                    </p>
                    <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
                      <Phone size={13} className="shrink-0 text-slate-400" /> {phone}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Working hours */}
            <div className="bg-sky-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={20} className="opacity-80" />
                <h4 className="font-semibold text-lg">Horario de Atención</h4>
              </div>
              <div className="space-y-2 text-sm">
                {[['Teléfono y Chat', '7 AM – 11 PM, de Lunes a Domingo'], ['Soporte por Correo', '7 AM – 11 PM, de Lunes a Domingo'], ['Visitas a la Oficina', '7 AM – 11 PM, de Lunes a Domingo']].map(([label, hours]) => (
                  <div key={label} className="flex justify-between items-center py-1.5 border-b border-white/20 last:border-0">
                    <span className="text-sky-100">{label}</span>
                    <span className="font-medium">{hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
